import { Assets, type Texture } from "pixi.js";
import type { Facing } from "@clubap/shared";
import {
  AVATAR_ART,
  AVATAR_PORTRAIT,
  ELEMENT_ART,
  GAME_ART,
  ITEM_ART,
  ROOM_BACKGROUNDS,
  type DirectionalFrames,
  type Frames,
} from "./manifest.js";
import type { ItemArt } from "./itemArt.js";

/** Fotogramas cargados por dirección. Sólo las direcciones que sí cargaron. */
export type LoadedClip = Map<Facing, Texture[]>;

export interface LoadedLayer {
  idle: LoadedClip;
  /** `null` si no se declaró caminata o ninguna imagen cargó. */
  walk: LoadedClip | null;
  tinted: boolean;
  /**
   * Sólo las capas de ítem la traen: dónde cae el dibujo sobre el cuerpo, por
   * dirección. Las del cuerpo no se colocan — son la referencia contra la que se
   * coloca todo lo demás.
   */
  placement?: ItemArt["placement"];
}

export interface LoadedArt {
  /** Keyed by room id. Missing entries fall back to procedural art. */
  backgrounds: Map<string, Texture>;
  /** `null` cuando no hay arte declarada o ninguna cargó: se usa el vectorial. */
  avatarLayers: LoadedLayer[] | null;
  /**
   * Una capa por ítem con arte. Un ítem sin entrada simplemente no se dibuja: se
   * puede comprar y equipar igual, sólo que nadie lo ve.
   */
  itemLayers: Map<string, LoadedLayer>;
  /**
   * Una textura por TIPO de objeto del mapa. Sin entrada, el objeto se dibuja con
   * su silueta a mano alzada, así que un tipo sin arte nunca deja un hueco.
   */
  props: Map<string, Texture>;
  /**
   * Arte de los minijuegos: `games.get("frozen")?.get("guineo")`.
   *
   * Se carga con todo lo demás y no cuando se abre el juego. Son ocho SVG de un kilo
   * contra un fondo de sala que pesa cien veces eso, y a cambio el puesto abre sin un
   * fotograma en blanco — que es lo que se vería si la descarga empezara al pulsar.
   */
  games: Map<string, Map<string, Texture>>;
  /** URL cruda; la tarjeta la muestra con <img> y maneja el error ahí. */
  portrait: string | null;
}

/**
 * A missing or broken asset must never take the game down — you will hit this
 * constantly while iterating on art. Warn and fall back instead.
 */
async function tryLoad(path: string): Promise<Texture | null> {
  try {
    return await Assets.load<Texture>(path);
  } catch (cause) {
    console.warn(`[art] no se pudo cargar ${path}; usando fallback`, cause);
    return null;
  }
}

async function loadFrames(frames: Frames): Promise<Texture[]> {
  const paths = typeof frames === "string" ? [frames] : frames;
  const loaded: Texture[] = [];

  for (const path of paths) {
    const texture = await tryLoad(path);
    // Un hueco en la secuencia haría saltar la animación, así que se omite.
    if (texture) loaded.push(texture);
  }

  return loaded;
}

/** Distingue "una secuencia para todas las direcciones" de "un mapa por dirección". */
function isDirectionMap(value: DirectionalFrames): value is Partial<Record<Facing, Frames>> {
  return typeof value !== "string" && !Array.isArray(value);
}

async function loadClip(clip: DirectionalFrames): Promise<LoadedClip> {
  const result: LoadedClip = new Map();

  if (!isDirectionMap(clip)) {
    const textures = await loadFrames(clip);
    if (textures.length > 0) result.set("s", textures);
    return result;
  }

  for (const [facing, frames] of Object.entries(clip)) {
    if (!frames) continue;
    const textures = await loadFrames(frames);
    if (textures.length > 0) result.set(facing as Facing, textures);
  }

  return result;
}

let cached: Promise<LoadedArt> | null = null;

export function loadArt(): Promise<LoadedArt> {
  cached ??= (async () => {
    const backgrounds = new Map<string, Texture>();

    for (const [roomId, path] of Object.entries(ROOM_BACKGROUNDS)) {
      if (!path) continue;
      const texture = await tryLoad(path);
      if (texture) backgrounds.set(roomId, texture);
    }

    let avatarLayers: LoadedLayer[] | null = null;

    if (AVATAR_ART) {
      const loaded: LoadedLayer[] = [];

      for (const layer of AVATAR_ART.layers) {
        const idle = await loadClip(layer.idle);
        // Sin pose quieta no hay referencia de escala ni fallback: capa inútil.
        if (idle.size === 0) continue;

        const walk = layer.walk ? await loadClip(layer.walk) : null;

        loaded.push({
          idle,
          walk: walk && walk.size > 0 ? walk : null,
          tinted: layer.tinted === true,
        });
      }

      if (loaded.length > 0) avatarLayers = loaded;
    }

    const itemLayers = new Map<string, LoadedLayer>();

    for (const [itemId, layer] of Object.entries(ITEM_ART)) {
      if (!layer) continue;

      const idle = await loadClip(layer.idle);
      if (idle.size === 0) continue;

      const walk = layer.walk ? await loadClip(layer.walk) : null;
      itemLayers.set(itemId, {
        idle,
        walk: walk && walk.size > 0 ? walk : null,
        tinted: layer.tinted === true,
        placement: layer.placement,
      });
    }

    const props = new Map<string, Texture>();

    for (const [kind, path] of Object.entries(ELEMENT_ART)) {
      if (!path) continue;
      const texture = await tryLoad(path);
      if (texture) props.set(kind, texture);
    }

    const games = new Map<string, Map<string, Texture>>();

    for (const [gameId, pieces] of Object.entries(GAME_ART)) {
      if (!pieces) continue;

      const loaded = new Map<string, Texture>();

      for (const [piece, path] of Object.entries(pieces)) {
        const texture = await tryLoad(path);
        if (texture) loaded.set(piece, texture);
      }

      if (loaded.size > 0) games.set(gameId, loaded);
    }

    return { backgrounds, avatarLayers, itemLayers, props, games, portrait: AVATAR_PORTRAIT };
  })();

  return cached;
}
